/**
 * JSONViewer Component
 *
 * Displays Moodle quiz responses in a structured, collapsible JSON format.
 * Shows questions with their answers, detected answer types (JavaScript, HTML, CSS, text),
 * and metadata like completion time, grade, etc.
 */

import React, { useEffect, useRef } from 'react';
import { Box, Typography, Paper, Chip, Divider, Alert } from '@mui/material';
import ReactJson from 'react-json-view';
import { Code, TextFields, Html, Css } from '@mui/icons-material';
import CodeViewer from './CodeViewer';
import { formatCode } from '../../utils/codeFormatter';
import { CodeSearchContext } from '../../contexts/CodeSearchContext';

interface Question {
  number: number;
  answer: string;
  type: 'javascript' | 'html' | 'css' | 'text';
}

interface JSONViewerProps {
  questions?: Question[];
  responses?: { [key: string]: string };
  metadata?: {
    status?: string;
    started?: string;
    completed?: string;
    duration?: number;
    grade?: number;
    [key: string]: any;
  };
}

const JSONViewer: React.FC<JSONViewerProps> = ({ questions, responses, metadata }) => {
  // Use search context to highlight the active question
  const searchContext = React.useContext(CodeSearchContext);

  // Ref to store references to each question card
  const questionRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  // Scroll to the active question when questionFilter changes
  useEffect(() => {
    if (searchContext?.questionFilter) {
      const questionElement = questionRefs.current.get(searchContext.questionFilter);
      if (questionElement) {
        // Delay to ensure rendering is complete
        setTimeout(() => {
          questionElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest'
          });
        }, 100);
      }
    }
  }, [searchContext?.questionFilter]);

  // Get icon for answer type
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'javascript':
        return <Code fontSize="small" />;
      case 'html':
        return <Html fontSize="small" />;
      case 'css':
        return <Css fontSize="small" />;
      default:
        return <TextFields fontSize="small" />;
    }
  };

  // Get color for answer type
  const getTypeColor = (type: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (type) {
      case 'javascript':
        return 'warning';
      case 'html':
        return 'error';
      case 'css':
        return 'info';
      default:
        return 'default';
    }
  };

  // Format duration (seconds) to readable string
  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  if (!questions && !responses) {
    return (
      <Alert severity="warning">
        No quiz data available to display
      </Alert>
    );
  }

  return (
    <Box>
      {/* Metadata Section */}
      {metadata && Object.keys(metadata).length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
          <Typography variant="subtitle2" gutterBottom fontWeight="bold">
            Quiz Metadata
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
            {metadata.status && (
              <Chip label={`Status: ${metadata.status}`} size="small" color="primary" />
            )}
            {metadata.grade !== undefined && metadata.grade !== null && (
              <Chip label={`Grade: ${metadata.grade}`} size="small" color="success" />
            )}
            {metadata.duration && (
              <Chip label={`Duration: ${formatDuration(metadata.duration)}`} size="small" />
            )}
            {metadata.started && (
              <Chip label={`Started: ${new Date(metadata.started).toLocaleString()}`} size="small" variant="outlined" />
            )}
            {metadata.completed && (
              <Chip label={`Completed: ${new Date(metadata.completed).toLocaleString()}`} size="small" variant="outlined" />
            )}
          </Box>
        </Paper>
      )}

      {/* Questions Section */}
      {questions && questions.length > 0 ? (
        <Box>
          <Typography variant="h6" gutterBottom>
            Questions & Answers ({questions.length} total)
          </Typography>
          {questions.map((question, index) => {
            const isActiveQuestion = searchContext?.questionFilter === question.number;
            return (
            <Paper
              key={question.number}
              ref={(el) => {
                if (el) {
                  questionRefs.current.set(question.number, el);
                }
              }}
              variant="outlined"
              sx={{
                p: 2,
                mb: 2,
                bgcolor: isActiveQuestion ? 'primary.50' : 'background.paper',
                border: isActiveQuestion ? 2 : 1,
                borderColor: isActiveQuestion ? 'primary.main' : 'divider',
                transition: 'all 0.3s'
              }}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Question {question.number}
                </Typography>
                <Chip
                  icon={getTypeIcon(question.type)}
                  label={question.type.toUpperCase()}
                  size="small"
                  color={getTypeColor(question.type)}
                />
              </Box>
              <Divider sx={{ mb: 1.5 }} />

              {/* Use CodeViewer with syntax highlighting for code answers */}
              {question.type !== 'text' && question.answer ? (
                <Box sx={{ maxWidth: '100%' }}>
                  <CodeViewer
                    code={formatCode(question.answer, question.type)}
                    language={question.type}
                    showLineNumbers={true}
                    questionNumber={question.number}
                  />
                </Box>
              ) : (
                /* Plain text display for text-type answers */
                <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
                  <Typography
                    variant="body2"
                    component="pre"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {question.answer || '(No answer provided)'}
                  </Typography>
                </Paper>
              )}
            </Paper>
            );
          })}
        </Box>
      ) : responses && (
        /* Fallback: Display raw responses if questions array not available */
        <Box>
          <Typography variant="h6" gutterBottom>
            Raw Responses
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, overflow: 'auto' }}>
            <ReactJson
              src={responses}
              theme="rjv-default"
              displayDataTypes={false}
              displayObjectSize={false}
              enableClipboard={true}
              collapsed={false}
              name="responses"
              iconStyle="circle"
            />
          </Paper>
        </Box>
      )}

      {/* Full JSON View (Collapsible) */}
      <Box mt={3}>
        <Typography variant="subtitle2" gutterBottom color="text.secondary">
          Full Data Structure (Advanced)
        </Typography>
        <Paper variant="outlined" sx={{ p: 2, overflow: 'auto', maxHeight: '400px' }}>
          <ReactJson
            src={{ questions, responses, metadata }}
            theme="rjv-default"
            displayDataTypes={false}
            displayObjectSize={true}
            enableClipboard={true}
            collapsed={1}
            name="moodle_data"
            iconStyle="circle"
          />
        </Paper>
      </Box>
    </Box>
  );
};

export default JSONViewer;
